import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Video, Copy, Check, ExternalLink, RefreshCw, Loader2,
  Clock, Award, AlertTriangle, Eye, Download, Mail, Send, Share2
} from 'lucide-react';
import { toast } from 'sonner';
import { generateInterviewLink, getCandidateInterviews, getInterviewResults, exportInterviewReport, createShareLink } from '../services/api';
import EmailComposeModal from './EmailComposeModal';
import { useRegion } from '../context/RegionContext';

export const InterviewLinkModal = ({ 
  isOpen, 
  onClose, 
  candidate, 
  jobId,
  jobTitle,
  hasQuestions 
}) => {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [newLink, setNewLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [viewingResults, setViewingResults] = useState(null);
  const [resultsData, setResultsData] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [showEmailCompose, setShowEmailCompose] = useState(false);
  const { region } = useRegion();

  useEffect(() => {
    if (isOpen && candidate && jobId) {
      fetchInterviews();
      setNewLink(null);
    }
  }, [isOpen, candidate, jobId]);

  const fetchInterviews = async () => {
    try {
      setLoading(true);
      const data = await getCandidateInterviews(jobId, candidate.id);
      setInterviews(data.sessions || []);
    } catch (error) {
      console.error('Error fetching interviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLink = async () => {
    if (!hasQuestions) {
      toast.error('Please generate interview questions first');
      return;
    }
    
    try {
      setGenerating(true);
      const data = await generateInterviewLink(jobId, candidate.id);
      setNewLink(data);
      toast.success('Interview link generated!');
      fetchInterviews();
      // Automatically open email compose after link generation
      setShowEmailCompose(true);
    } catch (error) {
      console.error('Error generating link:', error);
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Failed to generate interview link');
    } finally {
      setGenerating(false);
    }
  };

  const getInterviewUrl = (token) => {
    const publicUrl = (process.env.REACT_APP_PUBLIC_URL || '').replace(/\/+$/, '') || window.location.origin;
    return `${publicUrl}/interview/${token}`;
  };

  const handleSendEmail = (interview) => {
    // Send email for an existing interview
    const existingLink = { token: interview.token, session_id: interview.id };
    setNewLink(existingLink);
    setShowEmailCompose(true);
  };

  const handleCopyLink = (token) => {
    navigator.clipboard.writeText(getInterviewUrl(token));
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleViewResults = (interviewToken) => {
    // Open the comprehensive results page in a new tab
    window.open(`/interview/${interviewToken}`, '_blank');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'in_progress': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const handleDownloadReport = async (fmt = 'pdf') => {
    try {
      const blob = await exportInterviewReport(jobId, candidate.id, fmt);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = fmt === 'docx' ? 'docx' : 'pdf';
      a.download = `Interview_Report_${(candidate?.parsed_data?.name || 'Candidate').replace(/\s+/g, '_')}.${ext}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Report downloaded!');
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error(error?.response?.status === 404 ? 'No completed interview found' : 'Failed to download report');
    }
  };

  const handleShareResults = async (interviewToken) => {
    try {
      const result = await createShareLink(interviewToken, 7);
      const shareUrl = `${(process.env.REACT_APP_PUBLIC_URL || '').replace(/\/+$/, '') || window.location.origin}/shared/${result.share_token}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Shareable link copied to clipboard! Valid for 7 days.', { duration: 4000 });
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('No completed interview to share');
      } else {
        toast.error('Failed to create share link');
      }
    }
  };

  const parsed = candidate?.parsed_data || {};

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="interview-link-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Video className="h-5 w-5 text-primary" strokeWidth={1.5} />
            AI Video Interview - {parsed.name || 'Candidate'}
          </DialogTitle>
        </DialogHeader>

        {/* New Link Section */}
        {newLink && (
          <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-green-500/10 text-green-500 border-green-500/20">New Link</Badge>
              {newLink.attempt_number && <span className="text-xs text-muted-foreground">Attempt #{newLink.attempt_number}</span>}
            </div>
            <div className="flex items-center gap-2 mb-3">
              <Input 
                value={getInterviewUrl(newLink.token)}
                readOnly
                className="text-xs"
              />
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => handleCopyLink(newLink.token)}
              >
                {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => window.open(`/interview/${newLink.token}?recruiter=1`, '_blank')}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
            <Button
              className="w-full gap-2"
              variant="default"
              onClick={() => setShowEmailCompose(true)}
              data-testid="send-email-btn"
            >
              <Mail className="h-4 w-4" />
              Send Interview Email to Candidate
            </Button>
          </div>
        )}

        {/* Generate Button */}
        <Button 
          onClick={handleGenerateLink} 
          disabled={generating || !hasQuestions}
          className="w-full mb-4"
          data-testid="generate-interview-link-btn"
        >
          {generating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Generating...
            </>
          ) : (
            <>
              <Video className="h-4 w-4 mr-2" />
              {interviews.length > 0 ? 'Generate New Interview Link (Retry)' : 'Generate Interview Link'}
            </>
          )}
        </Button>

        {!hasQuestions && (
          <p className="text-xs text-destructive text-center mb-4">
            Please generate interview questions before creating interview links
          </p>
        )}

        {/* Download Report - show if any completed interview exists */}
        {interviews.some(i => i.status === 'completed') && (
          <div className="flex gap-2 mb-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => handleDownloadReport('pdf')}
              data-testid="download-interview-report-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Report (PDF)
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => handleShareResults(interviews.find(i => i.status === 'completed')?.token)}
              data-testid="share-interview-results-btn"
            >
              <Share2 className="h-4 w-4 mr-2" />
              Share Results
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground"
              onClick={() => handleDownloadReport('docx')}
              data-testid="download-interview-report-docx-btn"
            >
              DOCX
            </Button>
          </div>
        )}

        {/* Previous Interviews */}
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
          </div>
        ) : interviews.length > 0 && (
          <div>
            <h4 className="text-sm font-medium mb-2">Previous Interviews</h4>
            <ScrollArea className="h-[200px]">
              <div className="space-y-2">
                {interviews.map((interview) => (
                  <div 
                    key={interview.id}
                    className="p-3 border rounded-lg hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className={getStatusColor(interview.status)}>
                          {interview.status}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          Attempt #{interview.attempt_number}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        {interview.total_score !== null && (
                          <Badge variant="outline" className="text-xs">
                            <Award className="h-3 w-3 mr-1" />
                            {interview.total_score}
                          </Badge>
                        )}
                        {interview.proctoring_violations > 0 && (
                          <Badge variant="destructive" className="text-xs">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            {interview.proctoring_violations}
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-muted-foreground">
                        {interview.created_at ? new Date(interview.created_at).toLocaleDateString() : 'N/A'}
                      </span>
                      <div className="flex items-center gap-1">
                        {interview.status === 'completed' && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleShareResults(interview.token)}
                            data-testid={`share-interview-${interview.id}`}
                          >
                            <Share2 className="h-3.5 w-3.5 mr-1" />
                            Share
                          </Button>
                        )}
                        {interview.status !== 'pending' && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleViewResults(interview.token)}
                          >
                            <Eye className="h-3.5 w-3.5 mr-1" />
                            Results
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleCopyLink(interview.token)}
                        >
                          <Copy className="h-3.5 w-3.5 mr-1" />
                          Copy
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleSendEmail(interview)}
                          data-testid={`email-interview-${interview.id}`}
                        >
                          <Mail className="h-3.5 w-3.5 mr-1" />
                          Email
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

      </DialogContent>
    </Dialog>

    {/* Email Compose Modal */}
    <EmailComposeModal
      open={showEmailCompose}
      onClose={() => setShowEmailCompose(false)}
      candidateName={parsed.name}
      candidateEmail={parsed.email}
      jobTitle={jobTitle}
      jobId={jobId}
      candidateId={candidate?.id}
      interviewSessionId={newLink?.session_id || newLink?.id}
      interviewUrl={newLink ? getInterviewUrl(newLink.token) : ''}
      region={region}
    />
    </>
  );
};

export default InterviewLinkModal;
